// src/pages/Checkin.jsx
//
// Main page for the Student CHECK-IN tool — the start-of-year analogue of
// StudentCheckout.jsx. Tabs:
//   1. My Pending    — records with a pending item I'm authorized to clear
//   2. All Students  — all current-year records
//   3. Find Student  — search /students to start a new check-in
//   4. Archive       — past school years (read-only)
//
// Permissions and per-item config (department / audience / required / signers)
// come from /config/checkinPointPeople, edited in the Super Admin panel's
// "Student Check-In Permissions" accordion. We resolve that against the static
// CHECKIN_ITEMS catalog and thread the resolved items down to the table/detail.
//
// MS support mirrors checkout: pass defaultGradeFilter='ms' + hideGradeFilter
// + pageTitle from a thin wrapper to get an MS-scoped check-in view.

import React, { useEffect, useMemo, useState } from 'react';
import {
  collection, doc, query, where, onSnapshot, getDoc, getDocs,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';

import {
  RECORD_STATUS,
  DEFAULT_SCHOOL_YEAR,
  MS_GRADES,
  US_GRADES,
  KA_COLORS,
  buildCheckinRecordId,
  getResolvedCheckinItems,
  getMyPendingCheckinItems,
  hasAnyCheckinPermission,
} from '../utils/checkinHelpers';
import { CHECKIN_CONFIG_DOC } from '../config/checkinItems';
import { canAccessTool } from '../config/roles';
import { useAuth } from '../contexts/AuthContext';

import CheckinDashboardTable from '../components/CheckinDashboardTable';
import CheckinStudentDetail from '../components/CheckinStudentDetail';

const styles = {
  page: {
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    color: '#222', maxWidth: 1300, margin: '0 auto', padding: '16px 20px',
  },
  pageHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4, flexWrap: 'wrap' },
  pageTitle: { fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 700, color: KA_COLORS.navy, margin: 0 },
  yearBadge: { background: KA_COLORS.gold, color: '#fff', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700 },
  scopeBadge: { background: KA_COLORS.navy, color: '#fff', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700 },
  pageSubtitle: { fontSize: 13, color: '#666', margin: '0 0 16px' },
  tabBar: { display: 'flex', gap: 4, marginBottom: 16, borderBottom: `2px solid ${KA_COLORS.borderGray}`, flexWrap: 'wrap' },
  tab: (active) => ({
    padding: '10px 18px', background: 'none', border: 'none',
    borderBottom: `3px solid ${active ? KA_COLORS.red : 'transparent'}`,
    cursor: 'pointer', fontSize: 14, fontWeight: active ? 700 : 500,
    color: active ? KA_COLORS.navy : '#666', fontFamily: 'inherit',
    transition: 'border-color 0.15s, color 0.15s', marginBottom: -2,
  }),
  badge: {
    display: 'inline-block', background: KA_COLORS.red, color: '#fff',
    fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
    marginLeft: 6, verticalAlign: 'middle',
  },
  findWrapper: { background: '#fff', border: `1px solid ${KA_COLORS.borderGray}`, borderRadius: 6, padding: 16 },
  findInput: {
    width: '100%', padding: '10px 12px', border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4, fontSize: 14, boxSizing: 'border-box', marginBottom: 12,
  },
  findHint: { fontSize: 12, color: '#888', marginBottom: 12 },
  studentList: {
    listStyle: 'none', padding: 0, margin: 0,
    border: `1px solid ${KA_COLORS.borderGray}`, borderRadius: 4,
    maxHeight: 480, overflowY: 'auto',
  },
  studentItem: {
    padding: '10px 14px', borderBottom: `1px solid ${KA_COLORS.borderGray}`,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    cursor: 'pointer', transition: 'background 0.1s',
  },
  studentItemHover: { background: '#FFF5F5' },
  studentItemMeta: { fontSize: 11, color: '#888' },
  hasRecordPill: {
    fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
    background: '#D4EDDA', color: '#155724', textTransform: 'uppercase', letterSpacing: 0.4,
  },
  noRecordPill: {
    fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
    background: '#FFE0B2', color: '#E65100', textTransform: 'uppercase', letterSpacing: 0.4,
  },
  loadingState: { padding: 40, textAlign: 'center', color: '#666', fontStyle: 'italic' },
  noPermissions: {
    padding: 40, textAlign: 'center', background: '#FFF8E1',
    border: '1px solid #FFE082', borderRadius: 6, color: '#5D4037', margin: '20px 0',
  },
  archivePicker: {
    display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, padding: 12,
    background: KA_COLORS.lightGray, border: `1px solid ${KA_COLORS.borderGray}`, borderRadius: 6,
  },
  archiveLabel: { fontSize: 13, fontWeight: 600, color: '#333' },
  archiveSelect: {
    padding: '6px 10px', border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4, fontSize: 13, background: '#fff',
  },
};

// Scope helpers (MS/US) — same shape as checkout.
function scopeFilterRecords(records, scope) {
  if (scope === 'ms') return records.filter((r) => MS_GRADES.includes(r.grade));
  if (scope === 'us') return records.filter((r) => US_GRADES.includes(r.grade));
  return records;
}
function scopeFilterStudents(students, scope) {
  if (scope === 'ms') return students.filter((s) => MS_GRADES.includes(s.grade));
  if (scope === 'us') return students.filter((s) => US_GRADES.includes(s.grade));
  return students;
}

// =============================================================================
// Find Student tab
// =============================================================================

const FindStudentTab = ({ currentSchoolYear, onPickStudent, scope }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recordIds, setRecordIds] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'students'));
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          const an = `${a.lastName || ''} ${a.firstName || ''}`.toLowerCase();
          const bn = `${b.lastName || ''} ${b.firstName || ''}`.toLowerCase();
          return an.localeCompare(bn);
        });
        setStudents(list);
      } catch (err) {
        console.error('Load students failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!currentSchoolYear) return undefined;
    const q = query(collection(db, 'checkinRecords'), where('schoolYear', '==', currentSchoolYear));
    const unsub = onSnapshot(q, (snap) => {
      const ids = new Set();
      snap.forEach((d) => {
        const data = d.data();
        if (data.studentId) ids.add(String(data.studentId));
      });
      setRecordIds(ids);
    });
    return () => unsub();
  }, [currentSchoolYear]);

  const scopedStudents = useMemo(() => scopeFilterStudents(students, scope), [students, scope]);

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return scopedStudents.slice(0, 50);
    const q = searchTerm.trim().toLowerCase();
    return scopedStudents.filter((s) => {
      const name = `${s.firstName || ''} ${s.lastName || ''} ${s.name || ''}`.toLowerCase();
      const sis = String(s.sisId || s.id || '').toLowerCase();
      return name.includes(q) || sis.includes(q);
    }).slice(0, 100);
  }, [scopedStudents, searchTerm]);

  const scopeLabel = scope === 'ms' ? 'middle school students' : scope === 'us' ? 'upper school students' : 'students';

  return (
    <div style={styles.findWrapper}>
      <input
        type="text"
        style={styles.findInput}
        placeholder={`Search ${scopeLabel} by name or SIS ID…`}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      <p style={styles.findHint}>
        {loading
          ? 'Loading students…'
          : `${scopedStudents.length} ${scopeLabel} total. Click a student to open their check-in (or create one).`}
      </p>
      {!loading && (
        <ul style={styles.studentList}>
          {filtered.length === 0 ? (
            <li style={{ ...styles.studentItem, color: '#999', fontStyle: 'italic' }}>
              No students match your search.
            </li>
          ) : (
            filtered.map((student) => (
              <FindStudentRow
                key={student.id}
                student={student}
                hasRecord={recordIds.has(String(student.sisId || student.id))}
                onPick={() => onPickStudent(student)}
              />
            ))
          )}
        </ul>
      )}
    </div>
  );
};

const FindStudentRow = ({ student, hasRecord, onPick }) => {
  const [hover, setHover] = useState(false);
  const fullName = student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Unknown';
  return (
    <li
      style={{ ...styles.studentItem, ...(hover ? styles.studentItemHover : {}) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onPick}
    >
      <div>
        <div style={{ fontWeight: 600, color: KA_COLORS.navy }}>{fullName}</div>
        <div style={styles.studentItemMeta}>
          {[student.grade, student.cohort, student.sisId && `SIS ${student.sisId}`].filter(Boolean).join('  •  ')}
        </div>
      </div>
      <div>
        {hasRecord ? (
          <span style={styles.hasRecordPill}>Has record</span>
        ) : (
          <span style={styles.noRecordPill}>No record yet</span>
        )}
      </div>
    </li>
  );
};

// =============================================================================
// Archive tab
// =============================================================================

const ArchiveTab = ({
  systemSettings, archiveYear, setArchiveYear, archiveRecords, archiveLoading,
  user, resolvedItems, onRowClick, defaultGradeFilter, hideGradeFilter,
}) => {
  const archivedYears = systemSettings?.archivedSchoolYears || [];
  if (archivedYears.length === 0) {
    return (
      <div style={styles.noPermissions}>
        <strong>No archived years yet.</strong>
        <p style={{ margin: '6px 0 0', fontSize: 13 }}>
          Past school years will appear here once a superadmin archives the current year.
        </p>
      </div>
    );
  }
  return (
    <div>
      <div style={styles.archivePicker}>
        <span style={styles.archiveLabel}>School year:</span>
        <select style={styles.archiveSelect} value={archiveYear || ''} onChange={(e) => setArchiveYear(e.target.value || null)}>
          <option value="">— Select a year —</option>
          {archivedYears.map((yr) => <option key={yr} value={yr}>{yr}</option>)}
        </select>
        {archiveYear && (
          <span style={{ fontSize: 12, color: '#666' }}>
            {archiveRecords.length} record{archiveRecords.length === 1 ? '' : 's'}
          </span>
        )}
      </div>
      {!archiveYear && (
        <div style={{ ...styles.loadingState, fontStyle: 'normal' }}>
          Choose a school year above to view archived records.
        </div>
      )}
      {archiveYear && (archiveLoading ? (
        <div style={styles.loadingState}>Loading archive…</div>
      ) : (
        <CheckinDashboardTable
          records={archiveRecords}
          resolvedItems={resolvedItems}
          user={user}
          onRowClick={onRowClick}
          defaultGradeFilter={defaultGradeFilter}
          hideGradeFilter={hideGradeFilter}
          hideDeadColumns={false}
          emptyMessage="No records found for this archived year."
        />
      ))}
    </div>
  );
};

// =============================================================================
// Main page
// =============================================================================

const Checkin = ({
  defaultGradeFilter = 'all',
  hideGradeFilter = false,
  pageTitle = 'Student Check-In',
} = {}) => {
  const auth = useAuth();
  const user = useMemo(() => {
    if (!auth?.user) return null;
    const a = auth.user;
    const p = auth.profile || {};
    return {
      uid: a.uid,
      email: a.email || p.email || null,
      displayName: a.displayName || p.displayName || null,
      role: p.role || null,
      allowedTools: Array.isArray(p.allowedTools) ? p.allowedTools : [],
      toolOverrides: p.toolOverrides && typeof p.toolOverrides === 'object' ? p.toolOverrides : {},
      staffSisId: p.staffSisId ? String(p.staffSisId) : null,
      isSuperAdmin: !!auth.isSuperAdmin,
      isStudent: !!auth.isStudent,
    };
  }, [auth?.user, auth?.profile, auth?.isSuperAdmin]);

  const scope = useMemo(() => {
    if (defaultGradeFilter === 'ms') return 'ms';
    if (defaultGradeFilter === 'us') return 'us';
    return 'all';
  }, [defaultGradeFilter]);

  const [systemSettings, setSystemSettings] = useState(null);
  const [checkinConfig, setCheckinConfig] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [activeRecordId, setActiveRecordId] = useState(null);
  const [creatingRecord, setCreatingRecord] = useState(false);
  const [archiveYear, setArchiveYear] = useState(null);
  const [archiveRecords, setArchiveRecords] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);

  // System settings (current/archived years) — shared with checkout.
  useEffect(() => {
    const ref = doc(db, 'config', 'systemSettings');
    const unsub = onSnapshot(ref, (snap) => {
      setSystemSettings(snap.exists() ? snap.data() : { currentSchoolYear: DEFAULT_SCHOOL_YEAR, archivedSchoolYears: [] });
    }, (err) => {
      console.error('systemSettings subscribe failed:', err);
      setSystemSettings({ currentSchoolYear: DEFAULT_SCHOOL_YEAR, archivedSchoolYears: [] });
    });
    return () => unsub();
  }, []);

  // Check-in permission/config doc.
  useEffect(() => {
    const ref = doc(db, 'config', CHECKIN_CONFIG_DOC);
    const unsub = onSnapshot(ref, (snap) => {
      setCheckinConfig(snap.exists() ? snap.data() : { items: {} });
    }, (err) => {
      console.error('checkinPointPeople subscribe failed:', err);
      setCheckinConfig({ items: {} });
    });
    return () => unsub();
  }, []);

  const resolvedItems = useMemo(() => getResolvedCheckinItems(checkinConfig), [checkinConfig]);

  const currentSchoolYear = systemSettings?.currentSchoolYear;

  useEffect(() => {
    if (!currentSchoolYear) return undefined;
    setRecordsLoading(true);
    const q = query(collection(db, 'checkinRecords'), where('schoolYear', '==', currentSchoolYear));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.studentName || '').toLowerCase().localeCompare((b.studentName || '').toLowerCase()));
      setRecords(list);
      setRecordsLoading(false);
    }, (err) => {
      console.error('checkin records subscribe failed:', err);
      setRecordsLoading(false);
    });
    return () => unsub();
  }, [currentSchoolYear]);

  useEffect(() => {
    if (!archiveYear) return;
    setArchiveLoading(true);
    (async () => {
      try {
        const q = query(collection(db, 'checkinRecords'), where('schoolYear', '==', archiveYear));
        const snap = await getDocs(q);
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.studentName || '').toLowerCase().localeCompare((b.studentName || '').toLowerCase()));
        setArchiveRecords(list);
      } catch (err) {
        console.error('archive load failed:', err);
        setArchiveRecords([]);
      } finally {
        setArchiveLoading(false);
      }
    })();
  }, [archiveYear]);

  const scopedRecords = useMemo(() => scopeFilterRecords(records, scope), [records, scope]);
  const scopedArchiveRecords = useMemo(() => scopeFilterRecords(archiveRecords, scope), [archiveRecords, scope]);

  const canSignAnything = useMemo(
    () => hasAnyCheckinPermission(user, resolvedItems),
    [user, resolvedItems],
  );

  const myPendingRecords = useMemo(() => {
    if (!user) return [];
    return scopedRecords.filter((r) => getMyPendingCheckinItems(r, user, resolvedItems).length > 0);
  }, [scopedRecords, user, resolvedItems]);

  const defaultTab = canSignAnything ? 'my-pending' : 'all';
  const [tab, setTab] = useState(defaultTab);

  const handlePickStudent = async (student) => {
    if (!currentSchoolYear) return;
    const sisId = student.sisId || student.id;
    const recordId = buildCheckinRecordId(currentSchoolYear, sisId);
    try {
      const ref = doc(db, 'checkinRecords', recordId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setActiveRecordId(recordId);
        return;
      }
      if (!canSignAnything) {
        alert(
          "This student doesn't have a check-in record yet. Only users assigned " +
          'to at least one check-in item (or a superadmin) can create new records. ' +
          'Ask a superadmin to assign you, or have an authorized colleague start it.',
        );
        return;
      }
      setCreatingRecord(true);
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'createCheckinRecord');
      const result = await fn({ studentId: String(sisId), schoolYear: currentSchoolYear });
      setActiveRecordId(result?.data?.recordId || recordId);
    } catch (err) {
      console.error('handlePickStudent (checkin) failed:', err);
      alert(`Failed to create record: ${err.message || 'Unknown error.'}`);
    } finally {
      setCreatingRecord(false);
    }
  };

  if (!user) {
    return <div style={styles.page}><div style={styles.loadingState}>Loading…</div></div>;
  }

  const requiredToolId = scope === 'ms' ? 'ms_checkin' : 'checkin';
  if (!canAccessTool(requiredToolId, user)) {
    return (
      <div style={styles.page}>
        <div style={styles.noPermissions}>
          <strong>You don't have access to {pageTitle}.</strong>
          <p style={{ margin: '6px 0 0', fontSize: 13 }}>
            A superadmin must grant you access to this tool, or assign you to at
            least one check-in item.
          </p>
        </div>
      </div>
    );
  }

  if (activeRecordId) {
    return (
      <CheckinStudentDetail
        recordId={activeRecordId}
        user={user}
        resolvedItems={resolvedItems}
        onBack={() => setActiveRecordId(null)}
      />
    );
  }

  const scopeBadgeText = scope === 'ms' ? 'Grades 7–8' : scope === 'us' ? 'Grades 9–12' : null;

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>{pageTitle}</h1>
        {scopeBadgeText && <span style={styles.scopeBadge}>{scopeBadgeText}</span>}
        {currentSchoolYear && <span style={styles.yearBadge}>{currentSchoolYear}</span>}
      </div>
      <p style={styles.pageSubtitle}>
        Track and clear students' start-of-year check-in forms and clearances.
        {creatingRecord && <strong> Creating record…</strong>}
      </p>

      <div style={styles.tabBar}>
        {canSignAnything && (
          <button type="button" style={styles.tab(tab === 'my-pending')} onClick={() => setTab('my-pending')}>
            My Pending
            {myPendingRecords.length > 0 && <span style={styles.badge}>{myPendingRecords.length}</span>}
          </button>
        )}
        <button type="button" style={styles.tab(tab === 'all')} onClick={() => setTab('all')}>All Students</button>
        <button type="button" style={styles.tab(tab === 'find')} onClick={() => setTab('find')}>Find Student</button>
        <button type="button" style={styles.tab(tab === 'archive')} onClick={() => setTab('archive')}>Archive</button>
      </div>

      {tab === 'my-pending' && (recordsLoading ? (
        <div style={styles.loadingState}>Loading…</div>
      ) : (
        <CheckinDashboardTable
          records={myPendingRecords}
          resolvedItems={resolvedItems}
          user={user}
          onRowClick={(r) => setActiveRecordId(r.id)}
          defaultGradeFilter={defaultGradeFilter}
          hideGradeFilter={hideGradeFilter}
          emptyMessage="🎉 No students currently waiting on you. Nice work!"
        />
      ))}

      {tab === 'all' && (recordsLoading ? (
        <div style={styles.loadingState}>Loading…</div>
      ) : (
        <CheckinDashboardTable
          records={scopedRecords}
          resolvedItems={resolvedItems}
          user={user}
          onRowClick={(r) => setActiveRecordId(r.id)}
          defaultGradeFilter={defaultGradeFilter}
          hideGradeFilter={hideGradeFilter}
          emptyMessage="No check-in records have been created yet for this school year."
        />
      ))}

      {tab === 'find' && (
        <FindStudentTab currentSchoolYear={currentSchoolYear} onPickStudent={handlePickStudent} scope={scope} />
      )}

      {tab === 'archive' && (
        <ArchiveTab
          systemSettings={systemSettings}
          archiveYear={archiveYear}
          setArchiveYear={setArchiveYear}
          archiveRecords={scopedArchiveRecords}
          archiveLoading={archiveLoading}
          user={user}
          resolvedItems={resolvedItems}
          onRowClick={(r) => setActiveRecordId(r.id)}
          defaultGradeFilter={defaultGradeFilter}
          hideGradeFilter={hideGradeFilter}
        />
      )}
    </div>
  );
};

export default Checkin;